import { type MouseEvent, type RefObject, useCallback, useMemo, useRef } from 'react';

import { type ClipSpriteSheet } from '@air/api/types';
import { getClipSpritesheetFrameCount } from '@air/utils-clips';

import {
  type Dimension,
  getBackgroundPositionFromFrame,
  getSpriteStyles,
  SCRUBBABLE_THUMBNAIL_INDICATOR_WIDTH,
} from '~/utils/videoThumbnails';

export type UseVideoScrubbableThumbnailParams = {
  containerDimensions: Dimension;
  scrubRegionRef?: RefObject<HTMLElement>;
  spritesheet: ClipSpriteSheet | undefined;
};

export const useVideoScrubbableThumbnail = ({
  containerDimensions,
  scrubRegionRef,
  spritesheet,
}: UseVideoScrubbableThumbnailParams) => {
  const indicatorRef = useRef<HTMLDivElement>(null);
  const backgroundImageRef = useRef<HTMLDivElement>(null);

  const hasContainerSize = containerDimensions.width > 0 && containerDimensions.height > 0;

  const backgroundImageStyles = useMemo(() => {
    if (!spritesheet || !hasContainerSize) {
      return;
    }

    return getSpriteStyles({
      containerHeight: containerDimensions.height,
      containerWidth: containerDimensions.width,
      frame: 0,
      spritesheet,
    });
  }, [containerDimensions.height, containerDimensions.width, hasContainerSize, spritesheet]);

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      if (!hasContainerSize || !spritesheet) {
        return;
      }

      const { clientX, currentTarget } = event;
      const { left, width: containerWidth } = (scrubRegionRef?.current ?? currentTarget).getBoundingClientRect();

      const pos = containerWidth > 0 ? Math.max(0, Math.min(1, (clientX - left) / containerWidth)) : 0;
      const frameCount = getClipSpritesheetFrameCount(spritesheet);

      const currentFrame = Math.floor(pos * frameCount);
      const isLastFrame = currentFrame === frameCount;

      if (indicatorRef.current) {
        indicatorRef.current.style.display = 'block';
        indicatorRef.current.style.transform = `translateX(${
          pos * containerWidth - (isLastFrame ? SCRUBBABLE_THUMBNAIL_INDICATOR_WIDTH : 0)
        }px)`;
      }

      if (backgroundImageRef.current) {
        const { x, y } = getBackgroundPositionFromFrame({
          containerHeight: containerDimensions.height,
          containerWidth: containerDimensions.width,
          frame: currentFrame,
          spritesheet,
        });

        backgroundImageRef.current.style.backgroundPosition = `${x}px ${y}px`;
      }
    },
    [containerDimensions.width, containerDimensions.height, hasContainerSize, scrubRegionRef, spritesheet],
  );

  const onMouseLeave = useCallback(() => {
    if (backgroundImageRef.current) {
      backgroundImageRef.current.style.backgroundPosition = `0 0`;
    }

    if (indicatorRef.current) {
      indicatorRef.current.style.display = 'none';
      indicatorRef.current.style.transform = 'translateX(0px)';
    }
  }, []);

  return {
    backgroundImageRef,
    backgroundImageStyles,
    indicatorRef,
    onMouseLeave,
    onMouseMove,
  };
};
